// Blueprint-интро уровня: полноэкранный 2D-канвас — миллиметровка, контур
// целевого здания рисуется «чертёжной» обводкой, штамп ЭТАЖИ, растворение.
// Скипается тапом. Показ один раз на уровень (localStorage), повторно — с
// карты по кнопке «i». Чистый 2D поверх WebGL: бюджет кадров не трогает.

const SEEN_KEY = (id) => `etazhi_tower_bp_${id}`;

export function blueprintSeen(levelId) {
  try { return localStorage.getItem(SEEN_KEY(levelId)) === '1'; } catch (e) { return true; }
}
export function resetBlueprint(levelId) {
  try { localStorage.removeItem(SEEN_KEY(levelId)); } catch (e) { /* ок */ }
}

// Контур здания из конфига уровня: ярусы сужаются, шпиль — иглой
function buildOutline(level) {
  const pts = [];
  const floors = level.floors;
  const tiers = level.tiers;
  const w0 = 1;
  let x = w0 / 2;
  pts.push([-x, 0], [x, 0]); // основание слева-направо

  const right = [], left = [];
  let width = w0;
  for (let f = 0; f <= floors; f += 1) {
    if (tiers && f > 0 && f % tiers.every === 0) {
      const next = Math.max(width * tiers.shrink, tiers.min / level.blockWidth);
      right.push([width / 2, f / floors], [next / 2, f / floors]);
      left.push([-width / 2, f / floors], [-next / 2, f / floors]);
      width = next;
    }
  }
  right.push([width / 2, 1]);
  left.push([-width / 2, 1]);
  return { right, left, topWidth: width, spire: !!level.spire };
}

export function showBlueprint(level) {
  return new Promise((resolve) => {
    const host = document.getElementById('blueprint');
    if (!host) return resolve();
    const canvas = host.querySelector('canvas');
    const ctx = canvas.getContext('2d');
    // Сначала показать, потом мерить: у display:none размеры нулевые
    host.classList.remove('hidden');
    const W = (canvas.width = host.clientWidth * devicePixelRatio);
    const H = (canvas.height = host.clientHeight * devicePixelRatio);

    try { localStorage.setItem(SEEN_KEY(level.id), '1'); } catch (e) { /* ок */ }

    const outline = buildOutline(level);
    // Габариты чертежа: здание в центре, поля под подписи
    const bh = H * 0.52;
    const bw = Math.min(W * 0.34, bh * 0.5);
    const cx = W / 2;
    const by = H * 0.78; // основание

    const X = (u) => cx + u * bw;
    const Y = (v) => by - v * bh;

    let t0 = 0;
    let done = false;
    let raf = 0;

    function line(pts, progress) {
      // Обводка с прогрессом: рисуем ломаную до доли её полной длины
      let total = 0;
      for (let i = 1; i < pts.length; i++) {
        total += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
      }
      let budget = total * progress;
      ctx.beginPath();
      ctx.moveTo(X(pts[0][0]), Y(pts[0][1]));
      for (let i = 1; i < pts.length && budget > 0; i++) {
        const seg = Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
        const k = Math.min(1, budget / seg);
        ctx.lineTo(
          X(pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * k),
          Y(pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * k));
        budget -= seg;
      }
      ctx.stroke();
    }

    function frame(ts) {
      if (!t0) t0 = ts;
      const t = (ts - t0) / 1000;

      ctx.clearRect(0, 0, W, H);
      // Миллиметровка
      ctx.strokeStyle = 'rgba(255,255,255,0.07)';
      ctx.lineWidth = 1;
      const grid = 34 * devicePixelRatio;
      for (let gx = 0; gx < W; gx += grid) { ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, H); ctx.stroke(); }
      for (let gy = 0; gy < H; gy += grid) { ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(W, gy); ctx.stroke(); }

      // Контур: земля → правая стена → левая, штриховая «чертёжная» манера
      const p = Math.min(1, t / 1.4);
      ctx.strokeStyle = 'rgba(150,210,255,0.95)';
      ctx.lineWidth = 2.5 * devicePixelRatio;
      ctx.setLineDash([10 * devicePixelRatio, 6 * devicePixelRatio]);
      ctx.beginPath();
      ctx.moveTo(X(-0.9), Y(0));
      ctx.lineTo(X(0.9), Y(0));
      ctx.stroke();
      line([[0.5, 0], ...outline.right], p);
      line([[-0.5, 0], ...outline.left], p);
      if (p >= 1) { // крыша и шпиль в конце
        ctx.beginPath();
        ctx.moveTo(X(-outline.topWidth / 2), Y(1));
        ctx.lineTo(X(outline.topWidth / 2), Y(1));
        ctx.stroke();
        if (outline.spire) {
          ctx.beginPath();
          ctx.moveTo(X(0), Y(1));
          ctx.lineTo(X(0), Y(1) - bh * 0.08);
          ctx.stroke();
        }
      }
      ctx.setLineDash([]);

      // Выносная линия с этажностью
      ctx.strokeStyle = 'rgba(255,255,255,0.4)';
      ctx.lineWidth = 1.5 * devicePixelRatio;
      ctx.beginPath();
      ctx.moveTo(X(0.72), Y(0));
      ctx.lineTo(X(0.72), Y(p));
      ctx.stroke();
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.font = `${13 * devicePixelRatio}px ui-monospace, monospace`;
      ctx.textAlign = 'left';
      ctx.fillText(`${Math.round(level.floors * p)} эт.`, X(0.78), Y(p) + 4 * devicePixelRatio);

      // Штамп: вспышка «ЭТАЖИ» после обводки
      if (t > 1.5) {
        const k = Math.min(1, (t - 1.5) / 0.25);
        ctx.save();
        ctx.globalAlpha = k;
        ctx.translate(cx, H * 0.16);
        ctx.scale(1 + (1 - k) * 1.6, 1 + (1 - k) * 1.6);
        ctx.fillStyle = '#e2231a';
        ctx.font = `900 ${34 * devicePixelRatio}px -apple-system, 'Segoe UI', sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillText('ЭТАЖИ', 0, 0);
        ctx.globalAlpha = k * 0.9;
        ctx.fillStyle = '#fff';
        ctx.font = `700 ${16 * devicePixelRatio}px -apple-system, 'Segoe UI', sans-serif`;
        ctx.fillText(`Объект: ${level.name} · ${level.floors} этажей`, 0, 30 * devicePixelRatio);
        ctx.restore();
      }

      // Растворение и конец
      if (t > 2.6) {
        host.style.opacity = String(Math.max(0, 1 - (t - 2.6) / 0.4));
      }
      if (t > 3.05) return finish();
      raf = requestAnimationFrame(frame);
    }

    function finish() {
      if (done) return;
      done = true;
      cancelAnimationFrame(raf);
      host.classList.add('hidden');
      host.style.opacity = '1';
      host.removeEventListener('pointerdown', skip);
      resolve();
    }
    // Скип тапом: событие не должно уронить блок — оверлей глотает его сам
    function skip(e) { e.stopPropagation(); finish(); }
    host.addEventListener('pointerdown', skip);

    raf = requestAnimationFrame(frame);
  });
}
